import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { DEBOUNCE_TIME, EDGE_API } from '~/lib/constants';
import { OptInOption, OptInOptionResponse } from '~/lib/types/edge-api-types';
import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import { isEmail } from '@parcel2go/core-ui';
import { useAuth } from 'react-oidc-context';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { Account, CommunicationPreferences } from '~/lib/types/basket-types';
import useBasketSection from './use-basket-section';
import { BasketSections } from '~/lib/types/enums';
import { capturePromiseException } from '~/lib/metrics';
import useWorkflowActions from './use-workflow-actions';

export const LOCAL_COMMS_PREFS = 'LOCAL-COMMS-PREFS';

export type LocalCommunicationPreferences = {
  preferences: CommunicationPreferences;
};

export default function useOptIns() {
  const { workflowData, storeWorkflowData } = useWorkflowActions();
  const { user, isAuthenticated } = useAuth();
  const account = useBasketSection<Account>({ name: BasketSections.ACCOUNT });
  const { tenant, settings } = useTenant();
  const { track, session } = useAnalytics();
  const [emailState, setEmail] = useState<string | undefined>(account.data.email);

  useEffect(() => {
    if (account.data.email) {
      const t = setTimeout(() => setEmail(account.data.email), DEBOUNCE_TIME);
      return () => clearTimeout(t);
    }
  }, [account.data.email]);

  const value = useMemo(() => {
    const v = workflowData.get('COMMS');
    if (typeof v === 'object') {
      return v as CommunicationPreferences;
    }
    return {} as CommunicationPreferences;
  }, [workflowData]);

  const { data, isLoading } = useSWR<OptInOptionResponse>(
    `optins-${emailState}-${isAuthenticated}`,
    () => {
      if (!isEmail(emailState || '')) return Promise.resolve({ result: { options: [], contentKey: undefined } });
      return authenticatedFetchWithTenantAnalytics(
        user,
        tenant,
        settings,
        session,
        `${EDGE_API}/${tenant}/order-process/optins`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ email: emailState })
        }
      )
        .then((res) => res.json())
        .catch(capturePromiseException);
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  const updateLocal = useCallback(() => {
    const currentSelections = { ...value };

    // Add options which don't exist in storedValue
    data?.result?.options.forEach((option) => {
      if (currentSelections[option.communicationType] === undefined) {
        currentSelections[option.communicationType] = option.selected;
      }
    });

    // Remove selections that don't exist in data.result.options
    Object.keys(currentSelections).forEach((key) => {
      if (!data?.result?.options.some((option) => option.communicationType === key)) {
        delete currentSelections[key];
      }
    });

    storeWorkflowData('COMMS', currentSelections);
  }, [data, storeWorkflowData, value]);

  useEffect(() => {
    !isLoading && data && updateLocal();
  }, [data, isLoading]);

  const options: OptInOption[] = useMemo(() => {
    const output = data?.result?.options || [];
    output.forEach((option) => {
      if (value[option.communicationType] !== undefined) {
        option.selected = value[option.communicationType];
      }
    });
    return output;
  }, [data, value]);

  const onUpdate = useCallback(
    async (toggle: string) => {
      storeWorkflowData('COMMS', { ...value, [toggle]: !value[toggle] });
      track({ type: 'opt_in_status_changed', payload: { options, emailState, isAuthenticated } });
    },
    [storeWorkflowData, value, track, options, emailState, isAuthenticated]
  );

  const onClear = useCallback(() => {
    storeWorkflowData('COMMS', {});
  }, [storeWorkflowData]);

  return {
    update: onUpdate,
    options: options,
    loading: isLoading,
    contentKey: data?.result?.contentKey,
    clear: onClear
  };
}
